import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AlphaplanAdvancedFields from './AlphaplanAdvancedFields.tsx';
import {
    FORM_DEFAULT_VALUES,
    integrationToFormData,
    type AlphaplanFormData,
    type AlphaplanIntegration,
} from './types.ts';
import { validateBaseUrl, type AlphaplanValidationError } from './validation.ts';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import { SecretField } from '../../../core/components/Secret.tsx';

export interface AlphaplanIntegrationFormProps {
    integration?: AlphaplanIntegration;
    onSubmit: (data: AlphaplanFormData) => Promise<void>;
    formId?: string;
}

/** Shared create/edit form for the ALPHAPLAN connector (purchase-side: order confirmations and invoices). */
const AlphaplanIntegrationForm = ({
    integration,
    onSubmit,
    formId = 'alphaplan-integration-form',
}: AlphaplanIntegrationFormProps) => {
    const { t } = useTranslation('integrations');

    const form = useForm<AlphaplanFormData>({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    // react-hook-form expects `true` or the error message
    const toMessage = (error: AlphaplanValidationError | null) =>
        error ? t(`alphaplan.form.validation.${error}`) : true;

    useEffect(() => {
        if (!integration) return;
        form.reset(integrationToFormData(integration));
    }, [integration, form]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderConfirmationChannels = channelData?.orderConfirmationProcessingChannels || [];
    const invoiceChannels = channelData?.invoiceProcessingChannels || [];

    return (
        <form
            className="max-w-3xl w-full flex flex-col gap-6"
            onSubmit={form.handleSubmit((data) => onSubmit(data))}
            id={formId}
            noValidate
        >
            <Controller
                name="name"
                control={form.control}
                rules={{ required: t('alphaplan.form.requiredError') }}
                render={({ field }) => (
                    <FormField label={t('alphaplan.form.name')} error={formErrors.name} required>
                        <StringField {...field} maxLength={255} />
                    </FormField>
                )}
            />

            <Controller
                name="baseUrl"
                control={form.control}
                rules={{
                    required: t('alphaplan.form.requiredError'),
                    validate: (value) => toMessage(validateBaseUrl(value)),
                }}
                render={({ field }) => (
                    <FormField
                        label={t('alphaplan.form.baseUrl')}
                        error={formErrors.baseUrl}
                        helpText={t('alphaplan.form.baseUrlHelpText')}
                        required
                    >
                        <StringField {...field} maxLength={512} placeholder="https://<host>/alphaplan-rest-api" />
                    </FormField>
                )}
            />

            <Controller
                name="apiKey"
                control={form.control}
                rules={{ required: integration ? false : t('alphaplan.form.requiredError') }}
                render={({ field }) => (
                    <FormField
                        label={t('alphaplan.form.apiKey')}
                        error={formErrors.apiKey}
                        helpText={
                            integration?.hasApiKey
                                ? t('alphaplan.form.apiKeyStored')
                                : t('alphaplan.form.apiKeyHelpText')
                        }
                        required={!integration}
                    >
                        <SecretField
                            {...field}
                            secretName="apiKey"
                            secretId={integration?.id}
                            maxLength={255}
                            placeholder={
                                integration?.hasApiKey ? t('alphaplan.form.secretUnchangedPlaceholder') : undefined
                            }
                        />
                    </FormField>
                )}
            />

            <div className="text-lg font-semibold mt-2">{t('alphaplan.form.header.userSession')}</div>

            <div className="flex gap-4">
                <Controller
                    name="username"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            className="flex-1"
                            label={t('alphaplan.form.username')}
                            error={formErrors.username}
                            helpText={t('alphaplan.form.usernameHelpText')}
                        >
                            <StringField {...field} maxLength={255} />
                        </FormField>
                    )}
                />

                <Controller
                    name="password"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            className="flex-1"
                            label={t('alphaplan.form.password')}
                            error={formErrors.password}
                            helpText={integration?.hasPassword ? t('alphaplan.form.passwordStored') : undefined}
                        >
                            <SecretField
                                {...field}
                                secretName="password"
                                secretId={integration?.id}
                                maxLength={255}
                                placeholder={
                                    integration?.hasPassword
                                        ? t('alphaplan.form.secretUnchangedPlaceholder')
                                        : undefined
                                }
                            />
                        </FormField>
                    )}
                />
            </div>

            <div className="text-lg font-semibold mt-2">{t('alphaplan.form.header.orderConfirmations')}</div>

            <Controller
                name="orderConfirmationChannelIds"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('alphaplan.form.orderConfirmationChannelIds')}
                        error={formErrors.orderConfirmationChannelIds}
                        helpText={t('alphaplan.form.orderConfirmationChannelIdsHelpText')}
                    >
                        <CheckboxGroup
                            {...field}
                            options={orderConfirmationChannels.map((channel) => ({
                                value: channel.id,
                                label: channel.name,
                            }))}
                            onValueChange={(value) => field.onChange({ target: { value } })}
                        />
                    </FormField>
                )}
            />

            <div className="text-lg font-semibold mt-2">{t('alphaplan.form.header.invoices')}</div>

            <Controller
                name="invoiceChannelIds"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('alphaplan.form.invoiceChannelIds')}
                        error={formErrors.invoiceChannelIds}
                        helpText={t('alphaplan.form.invoiceChannelIdsHelpText')}
                    >
                        <CheckboxGroup
                            {...field}
                            options={invoiceChannels.map((channel) => ({ value: channel.id, label: channel.name }))}
                            onValueChange={(value) => field.onChange({ target: { value } })}
                        />
                    </FormField>
                )}
            />

            <Controller
                name="attachSourceDocuments"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('alphaplan.form.attachSourceDocuments')}
                        error={formErrors.attachSourceDocuments}
                        helpText={t('alphaplan.form.attachSourceDocumentsHelpText')}
                    >
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            <AlphaplanAdvancedFields form={form} integration={integration} toMessage={toMessage} />
        </form>
    );
};

export default AlphaplanIntegrationForm;
